// Articles API client — wired to the real backend (S9, Agent D).
//
// All paths use the `/api/v1/articles/...` prefix on top of the configured
// `http` axios instance (baseURL = http://localhost:5001 by default).
//
// Endpoints return `ActionResponse<T>` envelopes and are unwrapped here so
// callers receive clean typed values.

import { http, unwrap, unwrapCreatedId, unwrapList, type ActionResponse } from './http';
import type { PaginationInfo } from './http';
import {
  articleStatusFromNumber,
  articleStatusToNumber,
  bulkActionToNumber,
  normalizeArticleStatus,
  type Article,
  type ArticleListItem,
  type ArticleListQuery,
  type ArticleVersion,
  type ArticleWriteInput,
  type BulkArticleAction,
  type ReorderFeaturedItem,
} from '@features/articles/types';

// ---- Raw BE DTO shapes (mirror swagger) ------------------------------------

interface ArticleListItemDto {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  categoryId: string;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  isFeatured: boolean;
  featuredOrder: number | null;
  viewCount: number;
  status: number;
  publishedAt: string | null;
  updatedAt: string;
  rowVersion?: string | null;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface ArticleDto {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  categoryId: string;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  keywords: string | null;
  allowIndexing: boolean;
  isFeatured: boolean;
  featuredOrder: number | null;
  viewCount: number;
  status: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tagIds: string[] | null;
  rowVersion?: string | null;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface ArticleVersionDto {
  id: string;
  articleId: string;
  versionNumber: number;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  categoryId: string;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  tagIds: string[] | null;
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

// ---- Mappers ----------------------------------------------------------------

function mapListItem(d: ArticleListItemDto): ArticleListItem {
  return {
    id: d.id,
    title: d.title,
    slug: d.slug,
    excerpt: d.excerpt ?? '',
    categoryId: d.categoryId ?? null,
    authorId: d.authorId,
    coverImageUrl: d.coverImageUrl,
    readingTime: d.readingTime,
    isFeatured: d.isFeatured,
    featuredOrder: d.featuredOrder,
    viewCount: d.viewCount,
    status: articleStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    updatedAt: d.updatedAt,
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapArticle(d: ArticleDto): Article {
  return {
    id: d.id,
    title: d.title,
    slug: d.slug,
    excerpt: d.excerpt ?? '',
    body: d.body,
    categoryId: d.categoryId ?? null,
    authorId: d.authorId,
    coverImageUrl: d.coverImageUrl,
    readingTime: d.readingTime,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    keywords: d.keywords ?? null,
    allowIndexing: d.allowIndexing,
    isFeatured: d.isFeatured,
    featuredOrder: d.featuredOrder,
    viewCount: d.viewCount,
    status: articleStatusFromNumber(d.status),
    publishedAt: d.publishedAt,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    tagIds: d.tagIds ?? [],
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function mapVersion(d: ArticleVersionDto): ArticleVersion {
  return {
    id: d.id,
    articleId: d.articleId,
    versionNumber: d.versionNumber,
    title: d.title,
    slug: d.slug,
    excerpt: d.excerpt ?? '',
    body: d.body,
    categoryId: d.categoryId ?? null,
    authorId: d.authorId,
    coverImageUrl: d.coverImageUrl,
    readingTime: d.readingTime,
    metaTitle: d.metaTitle,
    metaDescription: d.metaDescription,
    canonicalTag: d.canonicalTag,
    customCode: d.customCode,
    allowIndexing: d.allowIndexing,
    tagIds: d.tagIds ?? [],
    authorUserId: d.authorUserId,
    authorEmail: d.authorEmail,
    changeNote: d.changeNote,
    createdAt: d.createdAt,
  };
}

// ---- Public API -------------------------------------------------------------

export interface ArticleListResult {
  items: ArticleListItem[];
  pagination: PaginationInfo | null;
  /** Convenience: total record count (mirror of `pagination.totalCount`). */
  total: number;
}

export const articlesApi = {
  async list(query: ArticleListQuery = {}): Promise<ArticleListResult> {
    const p = new URLSearchParams();
    if (query.search) p.set('search', query.search);
    if (query.categoryId) p.set('categoryId', query.categoryId);
    if (query.authorId) p.set('authorId', query.authorId);
    if (query.tagId) p.set('tagId', query.tagId);
    if (query.status)
      p.set('status', String(articleStatusToNumber(normalizeArticleStatus(query.status))));
    if (query.publishedOnly) p.set('publishedOnly', 'true');
    if (query.featuredOnly) p.set('featuredOnly', 'true');
    if (query.includeDeleted) p.set('includeDeleted', 'true');
    if (query.languageId) p.set('languageId', query.languageId);
    if (query.page) p.set('page', String(query.page));
    if (query.pageSize) p.set('pageSize', String(query.pageSize));
    if (query.sortBy) p.set('sortBy', query.sortBy);
    if (query.descending === false) p.set('descending', 'false');

    const qs = p.toString();
    const res = await http.get<ActionResponse<ArticleListItemDto>>(
      `/api/v1/articles${qs ? `?${qs}` : ''}`,
    );
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapListItem),
      pagination,
      total: pagination?.totalCount ?? items.length,
    };
  },

  async get(id: string): Promise<Article> {
    const res = await http.get<ActionResponse<ArticleDto>>(`/api/v1/articles/${id}`);
    return mapArticle(unwrap(res));
  },

  /** Create returns the BE-generated GUID (BE may answer with a bare id or the full entity). */
  async create(input: ArticleWriteInput): Promise<string> {
    const res = await http.post<ActionResponse<string | { id: string }>>(
      '/api/v1/articles',
      input,
    );
    return unwrapCreatedId(res);
  },

  async update(id: string, input: ArticleWriteInput): Promise<void> {
    const res = await http.put<ActionResponse<boolean>>(`/api/v1/articles/${id}`, input);
    unwrap(res);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`/api/v1/articles/${id}`);
    unwrap(res);
  },

  async submitReview(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/articles/${id}/submit-review`,
    );
    unwrap(res);
  },

  /** Approve = move InReview → Approved; pass `approve=false` to reject. */
  async approve(id: string, approve = true, note?: string): Promise<void> {
    const p = new URLSearchParams();
    p.set('approve', approve ? 'true' : 'false');
    if (note) p.set('note', note);
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/articles/${id}/approve?${p.toString()}`,
    );
    unwrap(res);
  },

  async publish(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/articles/${id}/publish`);
    unwrap(res);
  },

  async archive(id: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/articles/${id}/archive`);
    unwrap(res);
  },

  async listVersions(id: string): Promise<ArticleVersion[]> {
    const res = await http.get<ActionResponse<ArticleVersionDto>>(
      `/api/v1/articles/${id}/versions`,
    );
    const { items } = unwrapList(res);
    return items.map(mapVersion);
  },

  async restoreVersion(id: string, versionNumber: number): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/articles/${id}/versions/${versionNumber}/restore`,
    );
    unwrap(res);
  },

  async bulk(action: BulkArticleAction, ids: string[]): Promise<number> {
    const res = await http.post<ActionResponse<number>>('/api/v1/articles/bulk', {
      action: bulkActionToNumber(action),
      ids,
    });
    return unwrap(res);
  },

  async reorderFeatured(items: ReorderFeaturedItem[]): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      '/api/v1/articles/featured/reorder',
      {
        items: items.map((i) => ({
          id: i.id,
          order: i.featuredOrder,
          rowVersion: i.rowVersion ?? null,
        })),
      },
    );
    unwrap(res);
  },
};

export type { Article, ArticleListItem, ArticleVersion } from '@features/articles/types';
